/**
 * El audio de cada pronunciación de un heterónimo (misión 60 del catálogo
 * léxico, decisión 81): palabras que se escriben igual y se dicen distinto
 * según el significado (`live` «vivir» /lɪv/ y «en vivo» /laɪv/). El audio de
 * la palabra (`GET /word-audio/:word`) la lee sola y la voz elige cuál decir —
 * en la prueba, `close` y `bow` salían SIEMPRE con la del otro significado—,
 * así que para estas palabras el parlante de la ficha toca el audio de la
 * pronunciación que la ficha muestra escrita.
 *
 * Los audios se generaron UNA vez, fuera de la app, con la voz de diccionario
 * (Nora): cada pronunciación dicha al final de una frase corta con el
 * significado antes, la palabra recortada de la frase, y dos controles (un
 * reconocedor de fonemas y el transcriptor) que confirmaron que dice la que
 * tiene que decir. Son archivos estáticos de speak (`public/audio-significado/`)
 * con su lista en `senseAudioManifest.ts`: viajan con el deploy, no pasan por
 * el backend y no cuestan nada al tocarlos. Cómo se rehacen:
 * `docs/design/lexicon/AUDIO-SIGNIFICADO.md`.
 *
 * Lo que no está en la lista suena como siempre, con el audio de la palabra:
 * las palabras que no son heterónimos, y los heterónimos que quedaron sin
 * audios (`SENSE_AUDIO_MISSING`, cada uno con su motivo). Un test exige que cada
 * heterónimo del diccionario esté en una de las dos listas.
 *
 * Una entrada del diccionario suena con el audio de las palabras aunque tenga
 * un espacio: los verbos frasales (`give up`, misión 66 del catálogo léxico).
 * `speakText` decide por el espacio y los mandaba por el camino de las frases
 * de la charla (`POST /speak`: la voz del coach, sin guardar); la ficha sabe que
 * lo que muestra es una entrada y lo dice con `entry`. El backend lo acepta
 * sólo si está en el diccionario activo.
 */
import { speakText, speakWord } from './englishApi.js';
import { SENSE_AUDIO } from './senseAudioManifest.js';

/**
 * La URL del audio de `word` dicha con `ipa` (la pronunciación que muestra la
 * ficha, tal como viene del diccionario), o `null` si no hay: entonces suena
 * el audio de la palabra.
 */
export function senseAudioUrl(word: string, ipa: string | null | undefined): string | null {
  if (!ipa) return null;
  return SENSE_AUDIO[word]?.[ipa] ?? null;
}

/**
 * El audio para el parlante de la ficha: el de la pronunciación si la palabra
 * es un heterónimo con audios, y si no, el de la palabra (como siempre).
 * `entry`: lo que muestra la ficha es una entrada del diccionario (no una
 * frase de Lo mío), así que suena con el audio de las palabras aunque tenga un
 * espacio.
 *
 * Si la palabra está en la lista pero esta pronunciación no (el diccionario
 * cargado no coincide con el de los audios), suena el de la palabra y queda
 * un aviso en la consola: no hay audio mejor que ofrecer, pero el desfase no
 * pasa en silencio.
 */
export async function speakPronunciation(
  word: string,
  ipa: string | null | undefined,
  { entry }: { entry: boolean },
): Promise<Blob> {
  const url = senseAudioUrl(word, ipa);
  if (!url) {
    if (ipa && SENSE_AUDIO[word]) {
      console.warn(
        `[sense-audio] «${word}» tiene audios, pero no para /${ipa}/: suena el de la palabra`,
      );
    }
    return entry ? speakWord(word) : speakText(word);
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`sense-audio: ${url} respondió ${res.status}`);
  return res.blob();
}
